import { useId } from 'react';

import { Badge, Button, OptionGroup, OptionRow } from '@/design-system';
import { isNhgClassification, type NhgClassification, type Urgency } from '@/entities/finding';
import type { PhotoRecord } from '@/shared/offline';

import { surveyCopy } from '../copy';
import { usePhotoDrop } from '../hooks/usePhotoDrop';
import type { QuestionFinding, QuestionView } from '../model/view';
import { AmountPromptField } from './AmountPromptField';
import { FindingDetail } from './FindingDetail';
import { DropOverlay, PhotoButtons } from './PhotoPicker';
import { PhotoStrip } from './PhotoStrip';
import { ValueAnswer } from './ValueAnswer';

export interface QuestionCardProps {
    view: QuestionView;
    photos: readonly PhotoRecord[];
    note: string;
    /**
     * De bevinding van dit antwoord, als het er een is of als de inspecteur er
     * bedragen op overschreef: het bedrag, de urgentie en de NHG-classificatie.
     */
    finding: QuestionFinding | null;
    /**
     * Het bouwjaar uit de BAG, alleen bij een vraag die naar de bouwperiode vraagt
     * (`special: build_year`). Een hint boven de antwoorden, nooit een antwoord.
     */
    buildYear?: number | null;
    /** `selected`: wat de rij op het scherm zou worden - aan of uit. */
    onToggle: (optionId: number, selected: boolean) => void;
    onNoteChange: (text: string) => void;
    onLocationChange: (text: string) => void;
    /** `prompted`: de urgentie die een keuze zonder vast bedrag noemt (`view.amountPrompt`). */
    onAmountChange: (cents: number | null, prompted?: Urgency) => void;
    onUrgencyChange: (urgency: Urgency) => void;
    onNhgChange: (nhg: NhgClassification | null) => void;
    /** Getal- en tekstvragen: de ruwe invoer, bij `blur`. */
    onValueCommit: (raw: string) => void;
    onAddPhotos: (files: File[]) => void;
    onRemovePhoto: (clientUuid: string) => void;
    /** Een ingediende opname: alles is te lezen, niets te wijzigen. */
    readOnly?: boolean;
    /**
     * Een vraag die in een eerder onderdeel hoort, hier geopend: waar hij hoort, en
     * `onDone` om hem weer tot de regel met het antwoord in te klappen.
     */
    shared?: { place: string; onDone?: (() => void) | undefined } | undefined;
}

const MULTI_TYPES = new Set(['options_multi', 'options_single_multi']);
const VALUE_TYPES = new Set(['number', 'text', 'text_items']);

/**
 * Een antwoordrij in het getinte blok: rijen van 40 px zonder eigen kaartje.
 * Overal elders is 44 px de ondergrens; hier niet, op verzoek van de eigenaar:
 * een keurmeester tikt deze rij honderden keren per keuring en wil er sneller
 * doorheen. Vier pixels per rij is over tien antwoorden een halve schermhoogte
 * minder scrollen. Krappere padding wint daarbovenop alleen hoogte terug bij
 * een antwoord dat over twee regels loopt, en dat is precies waar het schuurde:
 * met een scheidingslijn ertussen; het gekozen antwoord vol in de accentkleur
 * met witte tekst, en het rondje of vinkje omgekeerd (wit op groen), zodat je
 * het gekozen antwoord in één blik terugziet. De rij zelf blijft de
 * `OptionRow` uit het ontwerpsysteem - de hele rij is de hitbox, de pijltjes
 * werken - alleen de kleuren zijn die van de opname.
 */
const ANSWER_ROW = [
    'rounded-none min-h-10 gap-[8px] px-[10px] py-[3px] text-[14px] leading-[1.25] bg-transparent text-[var(--text-reading)]',
    'hover:bg-[var(--surface-row-hover)]',
    'aria-checked:bg-[var(--accent)] aria-checked:text-[var(--text-on-dark)] aria-checked:hover:bg-[var(--accent-hover)]',
    '[&[aria-checked=true]>span:first-child]:border-[var(--surface-card)] [&[aria-checked=true]>span:first-child]:bg-[var(--accent)]',
    'focus-visible:outline-offset-[-3px] [&[aria-checked=true]]:focus-visible:outline-[var(--surface-card)]',
].join(' ');

/**
 * Eén vraag: de vraag boven, de antwoorden eronder, dan de foto's en - bij een
 * gebrek of aandachtspunt - het blok voor het rapport. Alles op dezelfde witte
 * kaart, met haarlijnen ertussen: een getinte kop tegen een getint antwoordblok
 * zette een rand tussen vraag en antwoord waar er inhoudelijk geen is, en je
 * moet er vloeiend doorheen kunnen lezen. De enige kleur die overblijft is het
 * gekozen antwoord, want dat is het enige wat je in één blik wilt terugzien. De rand is de status: neutraal, accent zodra er een
 * antwoord staat, en rood zodra een vraag met `Foto verplicht` beantwoord is
 * maar nog geen foto heeft. Nooit een blokkerende modal - de opname kan altijd
 * door, het rapport waarschuwt (principe 4).
 *
 * De kaart is zo kort als hij kan: de vraag staat op één regel naast de
 * fotoknoppen, de antwoorden beginnen er meteen onder en er zit geen ruimte
 * tussen die niets zegt. Wat de inspecteur aanraakt blijft 44 px hoog, met de
 * hele rij als hitbox (principe 1); alleen de omlijsting krimpt, zodat er meer
 * vragen tegelijk in beeld passen en er minder gescrold hoeft te worden.
 */
export function QuestionCard({
    view,
    photos,
    note,
    finding,
    buildYear = null,
    onToggle,
    onNoteChange,
    onLocationChange,
    onAmountChange,
    onUrgencyChange,
    onNhgChange,
    onValueCommit,
    onAddPhotos,
    onRemovePhoto,
    readOnly = false,
    shared,
}: QuestionCardProps) {
    const labelId = useId();
    const drop = usePhotoDrop(onAddPhotos);

    /* Zoals de API het telt: een beantwoorde vraag met te weinig foto's (`photo_min`, minstens één). */
    const photoMissing = view.photoRequired && view.answered && photos.length < view.photoMin;
    const isCheck = MULTI_TYPES.has(view.question.type);
    const showBuildYear = buildYear !== null && view.question.special === 'build_year';

    /*
     * De status is een streep links in plaats van een kader eromheen: de vragen
     * staan tegen elkaar aan als één lijst, zoals een keurmeester een checklist
     * afwerkt, en de kleur zegt nog steeds hetzelfde — rood als er een foto
     * ontbreekt, accent zodra er een antwoord staat.
     */
    const borderColor = photoMissing
        ? 'var(--danger-border)'
        : view.answered
          ? 'var(--accent)'
          : 'var(--border-subtle)';

    return (
        <article
            data-testid={`question-${view.key}`}
            data-photo-missing={photoMissing ? 'true' : 'false'}
            data-answered={view.answered ? 'true' : 'false'}
            data-dropzone={drop.active ? 'active' : undefined}
            className="relative overflow-hidden border-t border-l-[3px] border-t-[var(--border-subtle)] bg-[var(--surface-card)]"
            style={{ borderLeftColor: drop.active ? 'var(--accent)' : borderColor }}
            {...(readOnly ? {} : drop.handlers)}
        >
            {drop.active ? <DropOverlay /> : null}

            <div className="flex items-center gap-2 bg-[var(--surface-question-head)] py-[4px] pr-1 pl-[10px]">
                <div className="min-w-0 flex-1 py-[2px]">
                    <p
                        id={labelId}
                        className="m-0 font-[family-name:var(--font-display)] text-[14px] leading-[1.25] font-semibold text-pretty text-[var(--text-primary)]"
                    >
                        {view.question.label}
                    </p>
                    {view.photoRequired ? (
                        <Badge
                            variant="text"
                            tone={photoMissing ? 'alarm' : 'warn'}
                            weight={photoMissing ? 'medium' : 'regular'}
                            className="mt-[3px] px-[5px] py-0 text-[11px]"
                        >
                            {surveyCopy.question.photoRequired}
                        </Badge>
                    ) : null}
                    {shared ? (
                        <p className="m-0 mt-[3px] text-[12px] leading-[1.35] text-[var(--text-secondary)]">
                            {surveyCopy.shared.note(shared.place)}
                        </p>
                    ) : null}
                </div>

                <div className="flex flex-[0_0_auto] items-center">
                    {shared?.onDone ? (
                        <Button
                            variant="outline"
                            size="md"
                            className="mr-1"
                            onClick={shared.onDone}
                        >
                            {surveyCopy.shared.done}
                        </Button>
                    ) : null}

                    {readOnly ? (
                        <span className="px-2 py-[13px] text-[12px] text-[var(--text-secondary)]">
                            {surveyCopy.question.photoCount(photos.length)}
                        </span>
                    ) : (
                        <PhotoButtons count={photos.length} onAddPhotos={onAddPhotos} />
                    )}
                </div>
            </div>

            <div className="border-t border-[var(--border-subtle)] bg-[var(--surface-card)]">
                {showBuildYear ? (
                    <p className="m-0 border-b border-[var(--border-subtle)] px-3 py-[4px] text-[12px] leading-[1.35] text-[var(--text-secondary)]">
                        {surveyCopy.question.buildYearHint(buildYear)}
                    </p>
                ) : null}

                {VALUE_TYPES.has(view.question.type) ? (
                    <ValueAnswer
                        view={view}
                        labelledBy={labelId}
                        readOnly={readOnly}
                        onCommit={onValueCommit}
                    />
                ) : (
                    <OptionGroup
                        type={isCheck ? 'check' : 'radio'}
                        labelledBy={labelId}
                        hasSelection={isCheck ? undefined : view.answered}
                        className="gap-0 divide-y divide-[var(--border-subtle)] p-0"
                    >
                        {view.question.options.map((option, index) => (
                            <OptionRow
                                key={option.id}
                                index={index}
                                checked={view.selectedOptionIds.includes(option.id)}
                                disabled={readOnly}
                                onSelect={(selected) => onToggle(option.id, selected)}
                                className={ANSWER_ROW}
                            >
                                {option.label}
                            </OptionRow>
                        ))}
                    </OptionGroup>
                )}
            </div>

            {view.amountPrompt ? (
                <AmountPromptField
                    urgency={view.amountPrompt}
                    answer={view.answer}
                    questionLabel={view.question.label}
                    readOnly={readOnly}
                    onCommit={(cents) => onAmountChange(cents, view.amountPrompt ?? undefined)}
                />
            ) : null}

            <PhotoStrip photos={photos} {...(readOnly ? {} : { onAddPhotos, onRemovePhoto })} />

            {finding ? (
                <FindingDetail
                    question={view.question}
                    note={note}
                    location={finding.location}
                    amountCents={finding.amountCents}
                    urgency={finding.urgency}
                    nhg={isNhgClassification(finding.nhg) ? finding.nhg : null}
                    ownAmount={finding.ownAmount}
                    onNoteChange={onNoteChange}
                    onLocationChange={onLocationChange}
                    onAmountChange={onAmountChange}
                    onUrgencyChange={onUrgencyChange}
                    onNhgChange={onNhgChange}
                    readOnly={readOnly}
                />
            ) : null}
        </article>
    );
}
